import first from "lodash/first";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { isSCMPColumnist, isSCMPEditorial, isSCMPPR } from "~/components/author/helpers";
import { article as articleData } from "~/data";
import type {
  hooksContentHelperContent$data,
  hooksContentHelperContent$key,
} from "~/queries/__generated__/hooksContentHelperContent.graphql";

export const useContentHelper = (reference_: hooksContentHelperContent$key) => {
  const content = useFragment(
    graphql`
      fragment hooksContentHelperContent on Content {
        ... on Article {
          types {
            value {
              entityId
            }
          }
        }
        authors {
          entityId
          types
        }
        flag
      }
    `,
    reference_,
  );

  const sanitizeArticleTypeEntityIds = (types: hooksContentHelperContent$data["types"]) =>
    types?.flatMap(type => type?.value).map(type => type?.entityId ?? "") ?? [];

  const sanitizeAuthorTypes = (authors: hooksContentHelperContent$data["authors"]) =>
    authors?.flatMap(author => author?.types?.map(type => type ?? "") ?? "") ?? [];

  const sanitizedAuthorEntityIds = (authors: hooksContentHelperContent$data["authors"]) =>
    authors?.flatMap(author => author?.entityId ?? "") ?? [];

  /*
    Condition is after sanitized article data, there are one entityId and its first entityId is
    either type column or opinion
  */
  const isScmpOpinionType = useMemo(() => {
    const typeIds = sanitizeArticleTypeEntityIds(content.types);
    if (typeIds.length !== 1) return false;

    const firstArticleTypeId = first(typeIds) ?? "";
    return (
      articleData.types.opinion.entityId === firstArticleTypeId ||
      articleData.types.column.entityId === firstArticleTypeId
    );
  }, [content.types]);

  /*
    Condition is after sanitized article data, there are two entityIds and its first entityId is
    type column
  */
  const isEditorialOpinionType = useMemo(() => {
    const typeIds = sanitizeArticleTypeEntityIds(content.types);
    if (typeIds.length !== 2) return false;

    const firstArticleTypeId = first(typeIds) ?? "";
    return articleData.types.column.entityId === firstArticleTypeId;
  }, [content.types]);

  /*
    Condition is after sanitized article data, there are two entityIds and its first entityId is
    type column and it's second/last entityId is type editorial
  */
  const isEditorialHeadlineType = useMemo(() => {
    const typeIds = sanitizeArticleTypeEntityIds(content.types);
    if (typeIds.length !== 2) return false;

    const firstArticleTypeId = first(typeIds) ?? "";
    const lastArticleTypeId = last(typeIds) ?? "";
    return (
      articleData.types.column.entityId === firstArticleTypeId &&
      articleData.types.editorial.entityId === lastArticleTypeId
    );
  }, [content.types]);

  /*
    Condition is after sanitized article data, its first entityId belongs to allowedTypes
  */
  const isSpecialArticleType = useMemo(() => {
    const typeIds = sanitizeArticleTypeEntityIds(content.types);
    if (typeIds.length === 0) return false;

    const firstArticleTypeId = first(typeIds) ?? "";
    const allowedTypes: string[] = [
      articleData.types.analysis.entityId,
      articleData.types.explainer.entityId,
      articleData.types.factCheck.entityId,
      articleData.types.letters.entityId,
      articleData.types.obituary.entityId,
      articleData.types.profile.entityId,
      articleData.types.qa.entityId,
      articleData.types.review.entityId,
    ];
    return allowedTypes.includes(firstArticleTypeId);
  }, [content.types]);

  const isAuthorSCMPEditorial = useMemo(() => {
    const authorIds = sanitizedAuthorEntityIds(content.authors);
    return isSCMPEditorial(authorIds);
  }, [content.authors]);

  const isAuthorSCMPPR = useMemo(() => {
    const authorIds = sanitizedAuthorEntityIds(content.authors);
    return isSCMPPR(authorIds);
  }, [content.authors]);

  const isAuthorSCMPColumnist = useMemo(() => {
    const authorTypes = sanitizeAuthorTypes(content.authors);
    return isSCMPColumnist(authorTypes);
  }, [content.authors]);

  const isContentWithFlag = useMemo(() => !isEmpty(content.flag), [content.flag]);

  return {
    isAuthorSCMPColumnist,
    isAuthorSCMPEditorial,
    isAuthorSCMPPR,
    isContentWithFlag,
    isEditorialHeadlineType,
    isEditorialOpinionType,
    isScmpOpinionType,
    isSpecialArticleType,
  };
};
